import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnInit, Output, ViewChild, ViewEncapsulation } from '@angular/core';
import { UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { enumEntityModelCode } from '@data-layer/enums/entities';
import { INPUT_TYPES_MAP, InputTypeMap } from '@constants/input-types-map';
import { CompanyModel } from '@data-layer/models/companies';
import { DocumentModel } from '@data-layer/models/documents';
import { enumIdentifierQualifier } from '@app/data-layer/enums/common';
import { enumChargesDistributionType, enumTransportationMode } from '@app/data-layer/enums/entry-summaries';
import { DEFAULT_CURRENCY_CODE } from '@constants/currency';
import { CustomValidator } from '@helpers/custom-validator';
import { MissingFieldsComponent } from '@shared/missing-fields/components';
import { FormHelper } from '@helpers/form.helper';
import { ShipmentModel } from '@data-layer/models/common';
import { EntrySummaryModel } from '@data-layer/models/entry-summaries';
import { EsValidationsHelperService } from '../../../services';

@Component({
  selector: 'es-terms-of-sale-desktop',
  template: `
    <p class="sidebar-form-description">{{ 'LABELS.FREIGHT_PAYMENT_HINT' | translate }}</p>

    <form class="terms-of-sale-form" autocomplete="off" [formGroup]="formGroup" (ngSubmit)="saveData()">
      <div class="d-flex justify-content-between mb-4">
        <div class="card card-step bl-type" [ngClass]="{ 'is-selected': typeControl.value === enumChargesDistributionType.COLLECT }">
          <div class="card-header">
            <app-radio-button
              [control]="typeControl"
              [id]="'bl-type-importer'"
              [label]="'ENTITY.IMPORTER'"
              [value]="enumChargesDistributionType.COLLECT"
              [name]="'bl-type'"
            ></app-radio-button>
          </div>
          <div class="card-body px-3">
            <span>{{ 'LABELS.IMPORTER_HINT' | translate }}</span>
          </div>
        </div>
        <div class="card card-step bl-type" [ngClass]="{ 'is-selected': typeControl.value === enumChargesDistributionType.PREPAID }">
          <div class="card-header">
            <app-radio-button
              [control]="typeControl"
              [id]="'bl-type-exporter'"
              [label]="'LABELS.EXPORTER'"
              [value]="enumChargesDistributionType.PREPAID"
              [name]="'bl-type'"
            ></app-radio-button>
          </div>
          <div class="card-body px-3">
            <span>{{ 'LABELS.EXPORTER_HINT' | translate }}</span>
          </div>
        </div>
      </div>

      <ng-container *ngIf="typeControl.value === enumChargesDistributionType.COLLECT">
        <p class="mb-0" [innerHTML]="'LABELS.IMPORTER_DESCRIPTION' | translate"></p>
        <p>{{ 'LABELS.BL_TYPE_DESCRIPTION' | translate }}</p>

        <div class="form-row">
          <div class="col-12">
            <app-control-wrapper [control]="formGroup.get('freightCost')" [errors]="errors">
              <label for="charges" control-label>
                {{ 'FORM_LABEL.TOTAL_CHARGES' | translate }}
                <i class="fa fa-asterisk form-required" aria-hidden="true"></i>
              </label>
              <div control-wrapper class="d-flex">
                <input
                  id="charges"
                  class="form-control input-with-description-label"
                  type="text"
                  inputmode="numeric"
                  [formControl]="formGroup.get('freightCost')"
                  [pattern]="currencyInputMap.pattern"
                  [mask]="currencyInputMap.mask"
                  [thousandSeparator]="currencyInputMap.thousandSeparator"
                  [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                />
                <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
              </div>
            </app-control-wrapper>
          </div>
        </div>
      </ng-container>

      <ng-container *ngIf="typeControl.value === enumChargesDistributionType.PREPAID">
        <p class="mb-3" [innerHTML]="'LABELS.SHIPPING_TERMS_EXPORTER' | translate"></p>

        <div class="form-row">
          <div class="col-12">
            <app-control-wrapper [control]="formGroup.get('freightCost')" [errors]="errors" helper="FORM_HELPER.AT_LEAST_ONE_PER_HTS_CODE">
              <label for="freightCost" control-label>
                {{ 'FORM_LABEL.FREIGHT_COST' | translate }}
                <i class="fa fa-asterisk form-required" aria-hidden="true"></i>
              </label>
              <div control-wrapper class="d-flex">
                <input
                  id="freightCost"
                  class="form-control input-with-description-label"
                  type="text"
                  inputmode="numeric"
                  [formControl]="formGroup.get('freightCost')"
                  [pattern]="currencyInputMap.pattern"
                  [mask]="currencyInputMap.mask"
                  [thousandSeparator]="currencyInputMap.thousandSeparator"
                  [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                />
                <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
              </div>
            </app-control-wrapper>
          </div>

          <div class="col-12">
            <app-control-wrapper [control]="formGroup.get('insurance')" [errors]="errors">
              <label for="insurance" control-label>{{ 'FORM_LABEL.INSURANCE' | translate }}</label>
              <div control-wrapper class="d-flex">
                <input
                  id="insurance"
                  type="text"
                  class="form-control input-with-description-label"
                  formControlName="insurance"
                  inputmode="numeric"
                  [pattern]="currencyInputMap.pattern"
                  [mask]="currencyInputMap.mask"
                  [thousandSeparator]="currencyInputMap.thousandSeparator"
                  [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                />
                <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
              </div>
            </app-control-wrapper>
          </div>

          <div class="col-12">
            <app-control-wrapper class="w-100" [control]="formGroup.get('transportationCharge')" [errors]="errors">
              <label for="transportationCharge" control-label>
                {{ 'FORM_LABEL.TRANSPORTATION' | translate }}
              </label>
              <div control-wrapper class="form-row">
                <div class="col-12">
                  <div class="d-flex">
                    <input
                      id="transportationCharge"
                      class="form-control input-with-description-label"
                      type="text"
                      formControlName="transportationCharge"
                      inputmode="numeric"
                      [pattern]="currencyInputMap.pattern"
                      [mask]="currencyInputMap.mask"
                      [thousandSeparator]="currencyInputMap.thousandSeparator"
                      [appMaskErrorTooltip]="currencyInputMap.maskErrorTooltip"
                    />
                    <div class="form-control input-with-description-label-box">{{ DEFAULT_CURRENCY_CODE }}</div>
                  </div>
                </div>
              </div>
            </app-control-wrapper>
          </div>
        </div>

        <p class="mb-0">{{ 'LABELS.BL_TYPE_DESCRIPTION' | translate }}</p>
      </ng-container>

      <div class="sidebar-form-buttons">
        <button type="submit" class="btn btn-primary">{{ 'BUTTON.SAVE' | translate }}</button>
      </div>
    </form>

    <missing-fields [code]="'LABELS.ENTRY_SUMMARY'" (decline)="saveData(false)"></missing-fields>
  `,
  styleUrls: ['./es-terms-of-sale-desktop.component.scss'],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class EsTermsOfSaleDesktopComponent implements OnInit {
  @Input() public entrySummary: EntrySummaryModel;
  @Input() public shipment: ShipmentModel;
  @Output() public dataSaved: EventEmitter<ShipmentModel> = new EventEmitter<ShipmentModel>();

  public formGroup: UntypedFormGroup;
  public typeControl: UntypedFormControl;

  public DEFAULT_CURRENCY_CODE: string = DEFAULT_CURRENCY_CODE;
  public enumEntityModelCode = enumEntityModelCode;
  public enumChargesDistributionType = enumChargesDistributionType;
  public enumIdentifierQualifier = enumIdentifierQualifier;
  public currencyInputMap: InputTypeMap = INPUT_TYPES_MAP.CURRENCY;

  public company: CompanyModel;
  public attachedDocuments: DocumentModel[] = [];

  public errors: any;

  @ViewChild(MissingFieldsComponent, { static: false })
  public missingFieldModal: MissingFieldsComponent;

  constructor(private fb: UntypedFormBuilder, private fh: FormHelper, private esValidationsHelperService: EsValidationsHelperService) {}

  public ngOnInit(): void {
    this.formGroup = this.fb.group({
      transportationCharge: new UntypedFormControl(null, CustomValidator.updateControl('freightCost')),
      freightCost: new UntypedFormControl(null, [Validators.required]),
      chargesDistributionType: new UntypedFormControl(enumChargesDistributionType.PREPAID, CustomValidator.updateControl('freightCost')),
      insurance: new UntypedFormControl(null, CustomValidator.updateControl('freightCost')),
    });

    this.typeControl = this.formGroup.get('chargesDistributionType') as UntypedFormControl;

    this.formGroup.patchValue(this.shipment);
  }

  public saveData(needValidate: boolean = true) {
    if (needValidate && this.formGroup.invalid) {
      const invalidFields = this.fh.validateAllFields(this.formGroup);
      this.openModal(invalidFields);
      return;
    }

    this.dataSaved.emit(this.formGroup.value);
  }

  private openModal(data: any): void {
    this.missingFieldModal.openCustomModal(
      this.esValidationsHelperService.getEsSummaryModalModifiedData(this.entrySummary.transportationMode, data),
    );
  }

  public get isTruck(): boolean {
    return this.entrySummary?.transportationMode === enumTransportationMode.TRUCK;
  }
}
